import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const safetyTopics = [
  { title: 'Is AllChinaBuy Safe?', icon: 'ri-shield-check-line', desc: 'Understand the safety profile of AllChinaBuy, including platform security, payment protection, and user experiences. Learn what safety measures are in place and what risks you should be aware of.' },
  { title: 'Is AllChinaBuy Legit?', icon: 'ri-questionnaire-line', desc: 'Explore whether AllChinaBuy is a legitimate business entity. We examine business registration, warehouse operations, and community verification without making unsupported claims.' },
  { title: 'Payment Safety', icon: 'ri-secure-payment-line', desc: 'Learn about payment methods accepted by AllChinaBuy, how to protect your financial information, and what to do if you encounter payment issues. Use trusted payment methods with buyer protection.' },
  { title: 'Seller Verification', icon: 'ri-user-search-line', desc: 'Discover how to verify seller reliability before placing orders. Check seller ratings, read reviews, and avoid sellers with warning signs like unreasonably low prices or no transaction history.' },
  { title: 'QC Safety', icon: 'ri-file-search-line', desc: 'Understand why QC (Quality Check) photos are essential for safe buying. Learn how to inspect photos, identify red flags, and request additional images when needed.' },
  { title: 'Shipping Risks', icon: 'ri-truck-line', desc: 'Shipping internationally involves risks including customs seizures, lost packages, and damaged goods. Learn how to choose shipping methods, use insurance, and handle shipping problems.' },
  { title: 'Account Security', icon: 'ri-lock-line', desc: 'Protect your AllChinaBuy account with strong passwords, two-factor authentication, and careful management of personal information. Avoid phishing attempts and suspicious login requests.' },
  { title: 'Common Scams', icon: 'ri-error-warning-line', desc: 'Recognize common scams in the buying agent space, including fake websites, seller scams, and phishing attempts. Know the warning signs and how to protect yourself.' },
  { title: 'Refund Considerations', icon: 'ri-refund-line', desc: 'Understand the refund and return policies for AllChinaBuy orders. Learn the timeline for refund requests, what conditions apply, and how to document issues for disputes.' },
];

const faqs = [
  { q: 'Is AllChinaBuy safe for new buyers?', a: 'AllChinaBuy has established warehouse operations and QC processes, making it a functional platform for buying agent services. However, as with any international cross-border transaction, new buyers should start with small test orders, thoroughly review QC photos, use secure payment methods with buyer protection, and familiarize themselves with the full process before placing larger orders. Read our beginner guide for a step-by-step walkthrough.' },
  { q: 'What payment methods are safest to use?', a: 'Credit cards and PayPal generally offer the most buyer protection compared to direct bank transfers or cryptocurrency. These payment methods often allow dispute resolution if something goes wrong. Avoid using payment methods that lack recourse or traceability.' },
  { q: 'How can I verify a seller is trustworthy?', a: 'Check seller transaction history, read customer reviews if available, compare prices with other sellers (unreasonably low prices can be a red flag), and look for sellers with consistent product descriptions and clear photos. When in doubt, ask for additional product images or QC photos before the item is shipped from the seller to the warehouse.' },
  { q: 'What should I do if my package is lost or seized?', a: 'Contact AllChinaBuy customer support immediately with your tracking number and order details. If you purchased shipping insurance, file a claim according to the terms. For customs seizures, understand your country regulations and whether the items were restricted. Document everything for potential refund or reshipment requests.' },
  { q: 'How do I spot a fake AllChinaBuy website?', a: 'Always verify the website URL carefully. Fake websites may use slightly different spellings, different domain extensions, or copy the official design. Bookmark the official website and only access it through your saved bookmark. Be suspicious of websites sent through unsolicited messages or emails.' },
];

export default function SafetyPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">{t('nav.safety')}</span>
          </nav>
        </div>
      </div>

      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            AllChinaBuy Safety Center
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Learn about payment safety, seller verification, QC photos, account security, and how to avoid common scams when using AllChinaBuy. Your safety is our priority.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>Why Safety Matters</h2>
            <p>
              When shopping through international buying agents like AllChinaBuy, safety should be your top priority. Unlike domestic e-commerce where consumer protection laws are strong and familiar, cross-border purchasing involves additional layers of complexity including currency exchange, international shipping, customs inspections, and communication with overseas sellers. Understanding these risks and how to mitigate them is essential for a positive buying experience.
            </p>
            <p>
              This Safety Center provides comprehensive guidance on every aspect of safe buying through AllChinaBuy. From payment security and seller verification to shipping risk management and scam prevention, we cover the topics that matter most to buyers. All information is based on general best practices for international shopping and community experiences, not official AllChinaBuy policy. Always verify current policies directly with AllChinaBuy before placing orders.
            </p>
          </div>
        </div>
      </section>

      {/* Safety Topics Grid */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            Safety Topics
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {safetyTopics.map((topic, idx) => (
              <div key={idx} className="bg-background-50 rounded-xl p-6 border border-background-200/60 hover:border-primary-300 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mb-4">
                  <i className={`${topic.icon} text-xl text-primary-600`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{topic.title}</h3>
                <p className="text-foreground-500 text-sm leading-relaxed">{topic.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>Safety Checklist Before Ordering</h2>
            <p>
              Before placing any order through AllChinaBuy, run through this checklist to minimize risk:
            </p>
            <p>
              1. <strong>Verify the website URL</strong> - Ensure you are on the correct AllChinaBuy website and not a phishing imitation.
            </p>
            <p>
              2. <strong>Research the seller</strong> - Check ratings, reviews, and transaction history. Avoid sellers with no history or suspiciously low prices.
            </p>
            <p>
              3. <strong>Review product details</strong> - Read descriptions carefully, check all available photos, and confirm sizing information.
            </p>
            <p>
              4. <strong>Understand shipping costs</strong> - Use the shipping calculator and factor in potential customs duties.
            </p>
            <p>
              5. <strong>Request QC photos</strong> - Never skip quality inspection. Request detailed photos from multiple angles.
            </p>
            <p>
              6. <strong>Use secure payment</strong> - Prefer credit cards or PayPal with buyer protection over direct transfers.
            </p>
            <p>
              7. <strong>Start small</strong> - Your first order should be a test to learn the process before committing larger amounts.
            </p>
            <p>
              8. <strong>Document everything</strong> - Save screenshots, order confirmations, and tracking numbers for dispute resolution.
            </p>

            <h2>What to Do If Something Goes Wrong</h2>
            <p>
              If you encounter problems with an order, act quickly and methodically. First, contact AllChinaBuy customer support with your order number and a clear description of the issue. Provide screenshots and evidence to support your claim. If the issue involves payment fraud, contact your payment provider (credit card company or PayPal) immediately to dispute the charge. For suspected scams or phishing, report the incident to relevant authorities and warn the community to prevent others from falling victim.
            </p>
            <p>
              Remember that most issues can be resolved with clear communication and patience. Keep records of all interactions, and approach disputes calmly with factual evidence rather than emotional complaints. For more detailed guidance on specific issues, check our related guides on <a href={`/${lang}/shipping/`} className="text-primary-600 hover:underline">Shipping</a> and <a href={`/${lang}/guides/`} className="text-primary-600 hover:underline">Buying Guides</a>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-12 md:py-16">
        <div className="container-main max-w-3xl">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            Safety FAQ
          </h2>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-background-200/60 rounded-xl bg-background-50 overflow-hidden" itemScope itemType="https://schema.org/Question">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-4 md:p-5 text-left"
                >
                  <span className="font-medium text-foreground-900 text-sm md:text-base" itemProp="name">{faq.q}</span>
                  <i className={`ri-arrow-down-s-line text-lg text-primary-500 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}></i>
                </button>
                {openFaq === i && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5" itemScope itemType="https://schema.org/Answer">
                    <p className="text-foreground-600 text-sm leading-relaxed" itemProp="text">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="bg-white rounded-2xl p-8 md:p-10 border border-background-200/70 text-center">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              Stay Safe While Shopping
            </h3>
            <p className="text-foreground-600 mb-6 leading-relaxed">
              Apply the safety principles you have learned here. Browse products with confidence, verify sellers, and always use QC photos before shipping.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`/${lang}/products/`} className="btn-primary inline-flex items-center justify-center">
                View Products <i className="ri-arrow-right-line ml-2"></i>
              </a>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="btn-secondary inline-flex items-center justify-center">
                Buy It Now <i className="ri-arrow-right-up-line ml-2"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}